import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { dealsAPI } from '../api';
import Header from '../components/Header';
import PaidFeatureGate from '../components/PaidFeatureGate';
import { TrendingUp, Calendar, Tag, ChevronRight, Search, Filter, Bookmark, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const SavedDeals = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { lang } = useTranslation();
  
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');

  const loadDeals = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const userId = user?.user_id || 'demo_user';
      const response = await dealsAPI.getSaved(userId);
      setDeals(response.data.deals || []);
    } catch (error) {
      console.error('Failed to load saved deals:', error);
      toast.error(lang === 'en' ? 'Failed to load saved deals' : 'Error al cargar tratos guardados');
    } finally {
      setLoading(false);
    }
  }, [user, lang]);

  const handleDeleteDeal = async (e, savedDealId) => {
    e.stopPropagation(); // Prevent card click
    
    const confirmMessage = lang === 'en' 
      ? 'Delete this saved deal?' 
      : '¿Eliminar este deal guardado?';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const userId = user?.user_id || 'demo_user';
      await dealsAPI.deleteSaved(savedDealId, userId);
      setDeals(deals.filter(d => d.saved_deal_id !== savedDealId));
      toast.success(lang === 'en' ? 'Deal deleted' : 'Deal eliminado');
    } catch (error) {
      console.error('Failed to delete deal:', error);
      toast.error(lang === 'en' ? 'Failed to delete deal' : 'Error al eliminar deal');
    }
  };

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  const getDealBadge = (rating) => {
    const configs = {
      strong: { bg: 'bg-green-100', text: 'text-green-800', label: lang === 'en' ? 'Strong Deal' : 'Trato Fuerte' },
      moderate: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: lang === 'en' ? 'Moderate' : 'Moderado' },
      not_recommended: { bg: 'bg-red-100', text: 'text-red-800', label: lang === 'en' ? 'Weak' : 'Débil' }
    };
    const config = configs[rating] || configs.moderate;
    return (
      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredDeals = deals.filter(deal => {
    const matchesSearch = deal.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         deal.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRating = filterRating === 'all' || deal.deal_rating === filterRating;
    return matchesSearch && matchesRating;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <PaidFeatureGate
        feature="save_deals"
        featureDisplayName={lang === 'en' ? 'Saved Deals' : 'Tratos Guardados'}
        user={user}
        lang={lang}
        authIcon={Bookmark}
        authTitle={lang === 'en' ? 'Sign In to Access Saved Deals' : 'Inicia Sesión para Acceder a Tratos Guardados'}
        authDescription={lang === 'en' 
          ? 'Create a free account or sign in to save deals and access them from any device.'
          : 'Crea una cuenta gratuita o inicia sesión para guardar tratos y acceder desde cualquier dispositivo.'}
        redirectAfterAuth="/saved"
      >
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-7xl">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors min-h-[44px] py-2 -ml-1 px-1 touch-manipulation"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === 'en' ? 'Back' : 'Regresar'}
        </button>
        
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-1 sm:mb-2">
              {lang === 'en' ? 'Saved Deals' : 'Tratos Guardados'}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground">
              {deals.length} {deals.length === 1 ? (lang === 'en' ? 'property' : 'propiedad') : (lang === 'en' ? 'properties' : 'propiedades')} {lang === 'en' ? 'saved' : 'guardadas'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              placeholder={lang === 'en' ? 'Search by address or city...' : 'Buscar por dirección o ciudad...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-border rounded-lg focus:outline-none focus:border-secondary text-sm sm:text-base"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="px-3 sm:px-4 py-2.5 sm:py-3 border border-border rounded-lg focus:outline-none focus:border-secondary bg-white text-sm sm:text-base"
              data-testid="filter-rating"
            >
              <option value="all">{lang === 'en' ? 'All Ratings' : 'Todas'}</option>
              <option value="strong">{lang === 'en' ? 'Strong Deals' : 'Tratos Fuertes'}</option>
              <option value="moderate">{lang === 'en' ? 'Moderate' : 'Moderados'}</option>
              <option value="not_recommended">{lang === 'en' ? 'Weak' : 'Débiles'}</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 sm:py-20">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-secondary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">{lang === 'en' ? 'Loading saved deals...' : 'Cargando tratos guardados...'}</p>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-16 sm:py-20">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold mb-2">
              {deals.length === 0 
                ? (lang === 'en' ? 'No saved deals yet' : 'Sin tratos guardados')
                : (lang === 'en' ? 'No matching deals' : 'Sin coincidencias')}
            </h3>
            <p className="text-muted-foreground mb-6 px-4">
              {deals.length === 0 
                ? (lang === 'en' ? 'Start analyzing properties to build your deal pipeline' : 'Empieza a analizar propiedades para construir tu cartera')
                : (lang === 'en' ? 'Try adjusting your search or filter criteria' : 'Intenta ajustar tu búsqueda o filtros')}
            </p>
            <button
              onClick={() => navigate('/analyze')}
              className="px-6 py-3 bg-secondary text-white rounded-lg font-semibold hover:bg-blue-700 transition-all"
              data-testid="cta-analyze"
            >
              {lang === 'en' ? 'Analyze a Property' : 'Analizar Propiedad'}
            </button>
          </div>
        ) : (
          <div className="grid gap-3 sm:gap-4">
            {filteredDeals.map((deal, index) => {
              // A valuation isn't a "deal" being rated — hide the score + Weak/Strong badge.
              const isValuation = (deal.analysis_data?.strategy || deal.strategy || 'flip').toLowerCase() === 'valuation';
              return (
              <div
                key={deal.saved_deal_id || index}
                className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => navigate('/results', { state: { analysis: deal.analysis_data, fromSavedAnalysis: true } })}
                data-testid={`deal-card-${index}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-primary group-hover:text-secondary transition-colors truncate">
                        {deal.address || deal.analysis_data?.address}
                      </h3>
                      {!isValuation && getDealBadge(deal.deal_rating || deal.analysis_data?.deal_rating)}
                    </div>
                    <p className="text-muted-foreground mb-3 sm:mb-4 text-sm sm:text-base">
                      {deal.city || deal.analysis_data?.city}, {deal.state || deal.analysis_data?.state}
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
                      {!isValuation && (
                      <div>
                        <div className="text-xs sm:text-sm text-muted-foreground mb-1">{lang === 'en' ? 'Deal Score' : 'Puntuación'}</div>
                        <div className="text-xl sm:text-2xl font-bold text-primary">
                          {Math.round(deal.deal_score || deal.analysis_data?.deal_score || 0)}/100
                        </div>
                      </div>
                      )}
                      {/* Middle two metrics are strategy-aware — mirror the PDF so flip-only */}
                      {/* fields (Net Profit) never leak onto valuation/rental cards. */}
                      {(() => {
                        const strategy = deal.analysis_data?.strategy || deal.strategy || 'flip';
                        const fmt = (v) => (v ?? null) !== null ? `$${Math.round(v).toLocaleString()}` : '—';
                        const arv = deal.arv ?? deal.analysis_data?.arv;
                        const purchase = deal.purchase_price ?? deal.analysis_data?.purchase_price;

                        if (strategy === 'valuation') {
                          const equity = (arv != null && purchase != null) ? (arv - purchase) : null;
                          return (
                            <>
                              <div>
                                <div className="text-xs sm:text-sm text-muted-foreground mb-1">{lang === 'en' ? 'Estimated Value' : 'Valor Estimado'}</div>
                                <div className="text-xl sm:text-2xl font-bold">{fmt(arv)}</div>
                              </div>
                              <div>
                                <div className="text-xs sm:text-sm text-muted-foreground mb-1">{lang === 'en' ? 'Potential Equity' : 'Capital Potencial'}</div>
                                <div className={`text-xl sm:text-2xl font-bold ${equity != null && equity >= 0 ? 'text-success' : 'text-red-600'}`}>{fmt(equity)}</div>
                              </div>
                            </>
                          );
                        }

                        if (strategy === 'rental') {
                          const rental = deal.analysis_data?.rental_analysis;
                          const rent = rental?.monthly_rent;
                          const cashflow = rental?.monthly_cashflow;
                          return (
                            <>
                              <div>
                                <div className="text-xs sm:text-sm text-muted-foreground mb-1">{lang === 'en' ? 'Monthly Rent' : 'Renta Mensual'}</div>
                                <div className="text-xl sm:text-2xl font-bold">{fmt(rent)}</div>
                              </div>
                              <div>
                                <div className="text-xs sm:text-sm text-muted-foreground mb-1">{lang === 'en' ? 'Monthly Cash Flow' : 'Flujo Mensual'}</div>
                                <div className={`text-xl sm:text-2xl font-bold ${cashflow != null && cashflow >= 0 ? 'text-success' : 'text-red-600'}`}>{fmt(cashflow)}</div>
                              </div>
                            </>
                          );
                        }

                        // flip (default) — unchanged
                        const netProfit = deal.net_profit ?? deal.analysis_data?.flip_analysis?.net_profit;
                        return (
                          <>
                            <div>
                              <div className="text-xs sm:text-sm text-muted-foreground mb-1">ARV</div>
                              <div className="text-xl sm:text-2xl font-bold">{fmt(arv)}</div>
                            </div>
                            <div>
                              <div className="text-xs sm:text-sm text-muted-foreground mb-1">{lang === 'en' ? 'Net Profit' : 'Ganancia'}</div>
                              <div className={`text-xl sm:text-2xl font-bold ${netProfit != null && netProfit >= 0 ? 'text-success' : 'text-red-600'}`}>{fmt(netProfit)}</div>
                            </div>
                          </>
                        );
                      })()}
                      <div>
                        <div className="text-xs sm:text-sm text-muted-foreground mb-1">{lang === 'en' ? 'Purchase' : 'Compra'}</div>
                        <div className="text-xl sm:text-2xl font-bold">
                          ${(deal.purchase_price || deal.analysis_data?.purchase_price)?.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex lg:flex-col items-center lg:items-end justify-between lg:justify-start gap-2 pt-2 lg:pt-0 border-t lg:border-t-0 border-border">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      {new Date(deal.saved_at || deal.created_at).toLocaleDateString()}
                    </div>
                    {deal.tags && deal.tags.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-muted-foreground" />
                        {deal.tags.map((tag, i) => (
                          <span key={i} className="px-2 py-1 bg-slate-100 rounded text-xs">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleDeleteDeal(e, deal.saved_deal_id)}
                        className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors touch-manipulation"
                        title={lang === 'en' ? 'Delete' : 'Eliminar'}
                        data-testid={`delete-deal-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hidden lg:block" />
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      </PaidFeatureGate>
    </div>
  );
};

export default SavedDeals;
